import { useState } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { FormCloseCircle, UserForm } from 'entities/close-circle/ui/user-form'
import { DOCUMENT } from 'entities/user/module'

import { CloseCircleApi } from 'shared/api/close-circle'
import error_icon from 'shared/images/error.png'
import success_icon from 'shared/images/success_popup.png'
import { getErrorMessage } from 'shared/lib/errorText.ts'
import citizenshipStore from 'shared/store/citizenship-store'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import { useDialogBottomSheet } from '../../shared/ui/dialog-bottom-sheet/lib'

import s from './styles.module.scss'

enum RESULT_REQUEST {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export const CloseCircleCreate = observer(() => {
  const { t } = useTranslation()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const [loading, setLoading] = useState<boolean>(false)

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormCloseCircle>()

  const navigate = useNavigate()

  const closePopUp = (isSuccess?: boolean) => {
    if (isSuccess) {
      navigate(RoutingPaths.closeCircle.path(), { replace: true })
    }

    closeDialog()
  }

  const citizenshipsData = citizenshipStore?.citizenshipAvia?.map(
    (citizenship) => ({
      value: citizenship.code,
      name: citizenship.country,
    }),
  )

  const typesDocuments = [
    { name: t('id_passport'), value: DOCUMENT.ID },
    { name: t('passport'), value: DOCUMENT.PASSPORT },
    { name: t('birth_certificate'), value: DOCUMENT.BIRTH_CERTIFICATE },
  ]

  const onSubmit = async (data: FormCloseCircle) => {
    try {
      setLoading(true)

      await CloseCircleApi.setUserDocuments(data?.form)

      showPopUp(RESULT_REQUEST.SUCCESS)
    } catch (error: unknown) {
      const errorText = getErrorMessage(error)

      showPopUp(RESULT_REQUEST.ERROR, errorText)
    } finally {
      setLoading(false)
    }
  }

  const showPopUp = (result: RESULT_REQUEST, errorDescription?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={result === RESULT_REQUEST.SUCCESS ? success_icon : error_icon}
          title={
            result === RESULT_REQUEST.SUCCESS
              ? t('success_title')
              : t('error_popup')
          }
          description={
            result === RESULT_REQUEST.SUCCESS
              ? t('success_decreption')
              : errorDescription || t('error_popup_description')
          }
          button={t('apply')}
          onClick={() => closePopUp(result === RESULT_REQUEST.SUCCESS)}
        />
      ),
      isDialog: false,
    })
  }

  return (
    <div className={s.wrapper}>
      <HeaderGoBack
        title={t('addition')}
        buttonWhite
      />

      {loading ? (
        <Loader marginTop={200} />
      ) : (
        <form
          className={s.form}
          onSubmit={handleSubmit(onSubmit)}
        >
          <UserForm
            register={register}
            control={control}
            errors={errors}
            setValue={setValue}
            options={{
              showLabel: true,
              citizenships: citizenshipsData,
              typesDocuments: typesDocuments,
            }}
          />

          <ButtonContinue type="submit">
            <span className={s.text}>{t('save')}</span>
          </ButtonContinue>
        </form>
      )}
    </div>
  )
})
